import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useStore } from 'zustand';
import { cabinet } from '../../api/cabinet';
import { useSession } from '../../app/session';
import { useSite } from '../../app/site';
import { Alert } from '../../components/Alert';
import { COMPANY_FALLBACK, OTP_LENGTH } from '../../config';
import { useT, type DictKey } from '../../i18n';
import { formatCountdown, maskDestination } from '../../lib/format';
import { canResend, createOtpStore, isBlocked, secondsUntilResend, type Method, type OtpErrorCode, type OtpState } from './otpMachine';

const ERR: Record<OtpErrorCode, DictKey> = {
  empty: 'login.empty',
  invalid_destination: 'err.invalid_destination',
  code_incomplete: 'err.code_incomplete',
  invalid_code: 'err.invalid_code',
  rate_limited: 'err.rate_limited',
  network: 'err.network',
  generic: 'err.generic',
};

function useNow(active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

export function LoginPage() {
  const status = useSession((s) => s.status);
  const loadSession = useSession((s) => s.load);
  const navigate = useNavigate();
  const [store] = useState(() =>
    createOtpStore(cabinet, {
      onSuccess: () => {
        void loadSession().then(() => navigate('/cases', { replace: true }));
      },
    }),
  );
  const s = useStore(store);
  const t = useT();
  const now = useNow(s.step === 'code' || s.blockedUntil !== null);

  if (status === 'authed') return <Navigate to="/cases" replace />;

  const errorText = (st: OtpState): string | null => {
    if (!st.error) return null;
    if (st.error === 'rate_limited' && isBlocked(st, now)) return t('err.rate_limited_wait', { time: formatCountdown(secondsUntilResend(st, now)) });
    return t(ERR[st.error]);
  };

  return (
    <div className="max-w-lg">
      {s.step === 'code' || s.step === 'done' ? (
        <CodeStep s={s} now={now} errorText={errorText(s)} store={store} />
      ) : (
        <DestinationStep s={s} errorText={errorText(s)} store={store} />
      )}
    </div>
  );
}

type StoreT = ReturnType<typeof createOtpStore>;

function DestinationStep({ s, errorText, store }: { s: OtpState; errorText: string | null; store: StoreT }) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const { setMethod, setDestination, request } = store.getState();
  const busy = s.busy === 'requesting';
  const isPhone = s.method === 'phone';
  const errId = 'dest-error';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void request().then(() => {
      if (store.getState().step === 'destination') inputRef.current?.focus();
    });
  };

  const methods: { m: Method; label: DictKey }[] = [
    { m: 'phone', label: 'login.byPhone' },
    { m: 'email', label: 'login.byEmail' },
  ];

  return (
    <>
      <h1 className="text-[30px] leading-tight sm:text-[36px]">{t('login.title')}</h1>
      <p className="mt-5 text-[16px] leading-relaxed text-txt">{t('login.intro')}</p>

      <form className="mt-10" onSubmit={submit} noValidate>
        <fieldset>
          <legend className="text-[14px] text-lab">{t('login.method')}</legend>
          <div className="mt-3 grid grid-cols-2 border border-line" role="radiogroup">
            {methods.map(({ m, label }) => (
              <label
                key={m}
                className={`flex min-h-[52px] cursor-pointer items-center justify-center px-2 text-center text-[15px] transition-colors ${
                  s.method === m ? 'bg-fg text-bg' : 'text-txt hover:bg-hov'
                } has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-fg`}
              >
                <input type="radio" name="method" value={m} checked={s.method === m} onChange={() => setMethod(m)} className="sr-only" disabled={busy} />
                {t(label)}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="mt-8">
          <label htmlFor="destination" className="block text-[15px]">
            {t(isPhone ? 'login.phoneLabel' : 'login.emailLabel')}
          </label>
          <input
            ref={inputRef}
            id="destination"
            key={s.method}
            className="field-input"
            type={isPhone ? 'tel' : 'email'}
            inputMode={isPhone ? 'tel' : 'email'}
            autoComplete={isPhone ? 'tel' : 'email'}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder={t(isPhone ? 'login.phonePlaceholder' : 'login.emailPlaceholder')}
            value={s.destination}
            onChange={(e) => setDestination(e.target.value)}
            aria-invalid={errorText ? true : undefined}
            aria-describedby={`dest-hint${errorText ? ` ${errId}` : ''}`}
            disabled={busy}
          />
          <p id="dest-hint" className="mt-2 text-[14px] text-lab">
            {t('login.destHint')}
          </p>
        </div>

        {errorText ? (
          <div id={errId} className="mt-6">
            <Alert kind="error">{errorText}</Alert>
          </div>
        ) : null}

        <button type="submit" className="btn-solid mt-8 w-full" disabled={busy}>
          {busy ? t('login.sending') : t('login.submit')}
        </button>
      </form>
      <HelpLine />
    </>
  );
}

function CodeStep({ s, now, errorText, store }: { s: OtpState; now: number; errorText: string | null; store: StoreT }) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const { setCode, verify, request, editDestination } = store.getState();
  const verifying = s.busy === 'verifying' || s.step === 'done';
  const requesting = s.busy === 'requesting';
  const lastAutoSubmitted = useRef<string>('');

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Submit automatically once all digits are in (typed, pasted or filled by the phone's SMS autofill).
  useEffect(() => {
    if (s.code.length === OTP_LENGTH && s.busy === 'idle' && s.code !== lastAutoSubmitted.current) {
      lastAutoSubmitted.current = s.code;
      void verify();
    }
    if (s.code.length < OTP_LENGTH) lastAutoSubmitted.current = '';
  }, [s.code, s.busy, verify]);

  // After a wrong code the field is cleared: put the cursor back.
  useEffect(() => {
    if (s.error === 'invalid_code') inputRef.current?.focus();
  }, [s.error]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void verify();
  };

  const wait = secondsUntilResend(s, now);
  const resendable = canResend(s, now);
  const dest = maskDestination(s.destination);

  return (
    <>
      <h1 className="text-[30px] leading-tight sm:text-[36px]">
        {t('login.codeTitle')}
      </h1>
      <p className="mt-5 text-[16px] leading-relaxed text-txt">
        {t(s.method === 'phone' ? 'login.codeSentPhone' : 'login.codeSentEmail', { dest })} {t('login.codeNote')}
      </p>

      <form className="mt-10" onSubmit={submit} noValidate>
        <label htmlFor="otp" className="block text-[15px]">
          {t('login.codeLabel')}
        </label>
        <input
          ref={inputRef}
          id="otp"
          name="otp"
          className="field-input text-center text-[30px] tracking-[.5em]"
          style={{ paddingLeft: '.5em' }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          enterKeyHint="go"
          value={s.code}
          onChange={(e) => setCode(e.target.value)}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (text) {
              e.preventDefault();
              setCode(text);
            }
          }}
          aria-invalid={errorText ? true : undefined}
          aria-describedby={errorText ? 'otp-error' : undefined}
          disabled={verifying}
        />

        <div className="mt-6 min-h-[1px]" aria-live="polite">
          {errorText ? (
            <div id="otp-error">
              <Alert kind="error">{errorText}</Alert>
            </div>
          ) : s.notice === 'resent' ? (
            <Alert kind="success">{t('login.resent')}</Alert>
          ) : null}
        </div>

        <button type="submit" className="btn-solid mt-6 w-full" disabled={verifying || s.code.length !== OTP_LENGTH}>
          {verifying ? t('login.verifying') : t('login.verify')}
        </button>
      </form>

      <div className="mt-8 flex flex-col items-start gap-2">
        {wait > 0 && !requesting ? (
          <p className="min-h-tap py-3 text-[15px] text-lab">{t('login.resendIn', { time: formatCountdown(wait) })}</p>
        ) : (
          <button type="button" className="link" onClick={() => void request()} disabled={!resendable}>
            {requesting ? t('login.sending') : t('login.resend')}
          </button>
        )}
        <button type="button" className="link" onClick={editDestination} disabled={verifying || requesting}>
          {t('login.change')}
        </button>
      </div>
      <HelpLine />
    </>
  );
}

function HelpLine() {
  const t = useT();
  const site = useSite((s) => s.site);
  // заглушку з конфігурації не показуємо: телефон зʼявиться, коли його вкажуть у налаштуваннях сайту в CRM
  const phone = site?.duty_phone || site?.phone || (COMPANY_FALLBACK.phone.includes('000 00 00') ? '' : COMPANY_FALLBACK.phone);
  if (!phone) return null;
  return (
    <p className="mt-12 border-t border-line pt-5 text-[15px] text-txt">
      {t('login.help')}{' '}
      <a className="whitespace-nowrap text-fg underline decoration-line underline-offset-4" href={`tel:${phone.replace(/[^\d+]/g, '')}`}>
        {phone}
      </a>
    </p>
  );
}
